<div class="card">
  @if (loadingContent) {
    <content-loading-list />
  }
  @if (!loadingContent && pager.total === 0) {
    <empty-records-component
      [message]="'kanban.cardList.emptyMessage' | translate"
      [subMessage]="'kanban.cardList.emptySubMessage' | translate"
      [icon]="'mdi mdi-alert-circle-outline'"
    />
  }
  @if (!loadingContent && pager.total > 0) {
    <div class="card-body table-responsive">
      <table class="table table-hover table-centered mb-0">
        <thead>
          <tr>
            <th class="text-primary-emphasis">{{ 'kanban.cardList.columnName' | translate }}</th>
            <th class="text-primary-emphasis">{{ 'kanban.cardList.columnCustomer' | translate }}</th>
            <th class="text-primary-emphasis">{{ 'kanban.cardList.columnValue' | translate }}</th>
            <th class="text-primary-emphasis">{{ 'kanban.cardList.columnResponsible' | translate }}</th>
            <th class="text-primary-emphasis">{{ 'kanban.cardList.columnStage' | translate }}</th>
            <!--
              Same signals the card carries, so board and list are readings of one
              working list rather than two different products (RF-20, AC-09).
            -->
            <th class="text-primary-emphasis text-center">{{ 'kanban.cardList.columnScore' | translate }}</th>
            <th class="text-primary-emphasis">{{ 'kanban.cardList.columnUrgency' | translate }}</th>
            <th class="text-primary-emphasis">{{ 'kanban.cardList.columnLastInteraction' | translate }}</th>
            <th class="text-primary-emphasis">{{ 'kanban.cardList.columnProducts' | translate }}</th>
            <th class="sr-only"></th>
          </tr>
        </thead>
        <tbody>
          @for (item of pager.list; track item) {
            <tr
              class="cursor-pointer"
              (click)="openDetailCard(item)"
              [ngClass]="{
                'bg-danger bg-opacity-25': item?.priority === 'CRITICAL',
              }"
            >
              <td>
                <span>
                  <div>
                    @if (item?.name?.length > 30) {
                      <span matTooltip="{{ item?.name }}">
                        {{ $safeNavigationMigration(item?.name) | truncate: 30 }}
                      </span>
                    } @else {
                      <span>
                        {{ item?.name }}
                      </span>
                    }
                    <div class="d-flex align-items-center mt-1">
                      <span
                        class="badge px-2 font-11 fw-bold d-flex align-items-center justify-content-center"
                        [ngClass]="{
                          'bg-danger text-white':
                            item?.priority === 'URGENT' ||
                            item?.priority === 'CRITICAL',
                          'bg-warning text-dark': item?.priority === 'HIGH',
                          'bg-info text-white': item?.priority === 'MEDIUM',
                          'bg-success text-white': item?.priority === 'LOW',
                          'bg-secondary text-white': item?.priority === 'NONE',
                        }"
                      >
                        {{ translateEnums(item.priority) | translate }}
                      </span>
                      @if (item?.isPrivate) {
                        <span
                          [matTooltip]="'kanban.cardList.privateOpportunity' | translate"
                          class="ms-1 z-100 d-inline-flex align-items-center"
                        >
                          <i
                            class="mdi mdi-lock-outline text-danger fs-4 lh-1"
                          ></i>
                        </span>
                      }
                      @if (item?.cloned) {
                        <span class="badge bg-warning text-white ms-1">
                          {{ 'kanban.cardList.cloned' | translate }}
                        </span>
                      }
                      <!-- Informative only (SPEC-2026-10 RF-21): a closed deal of a blacklisted contact keeps counting. -->
                      @if (item?.leadFlags?.blacklisted === true && (item?.won === true || item?.won === false)) {
                        <span
                          class="badge bg-soft-dark text-dark ms-1"
                          [matTooltip]="'kanban.cardList.blacklistedTooltip' | translate"
                        >
                          {{ 'kanban.cardList.blacklisted' | translate }}
                        </span>
                      }
                      <div class="ms-1">
                        @if (item?.temperature === CardTemperatureEnum.HOT) {
                          <i
                            class="ri-fire-fill font-18 text-danger"
                            [matTooltip]="'kanban.cardList.temperatureHot' | translate"
                          ></i>
                        } @else if (
                          item?.temperature === CardTemperatureEnum.WARM
                        ) {
                          <i
                            class="ri-sun-cloudy-fill font-18 text-dark text-opacity-50"
                            [matTooltip]="'kanban.cardList.temperatureWarm' | translate"
                          ></i>
                        } @else if (
                          item?.temperature === CardTemperatureEnum.COLD
                        ) {
                          <i
                            class="uil-snowflake font-18 text-info"
                            [matTooltip]="'kanban.cardList.temperatureCold' | translate"
                          ></i>
                        } @else {
                          <i
                            class="mdi mdi-thermometer font-18 text-muted"
                            [matTooltip]="'kanban.cardList.temperatureUndefined' | translate"
                          ></i>
                        }
                      </div>
                    </div>
                  </div>
                </span>
              </td>
              <td>
                <span>
                  @if (item?.customer?.name?.length > 15) {
                    <span matTooltip="{{ item?.customer?.name }}">
                      {{ $safeNavigationMigration(item?.customer?.name) | truncate: 15 }}
                    </span>
                  } @else {
                    <span>
                      {{ item?.customer?.name }}
                    </span>
                  }
                </span>
              </td>
              <td>
                <span>
                  {{
                    item.budget
                      ? (item.budget
                        | currency: "BRL" : "symbol-narrow" : "1.2-2")
                      : "R$ 0,00"
                  }}
                </span>
              </td>
              <td>
                <div class="d-flex align-items-center">
                  <img
                    [src]="getUserImage(item.user ?? null)"
                    class="avatar-xs rounded-circle border me-2"
                    (error)="onImageError($event)"
                  />
                  <span class="fw-semibold text-dark font-14 text-truncate">
                    {{ item.user?.name ?? ('kanban.cardList.noResponsible' | translate) }}
                  </span>
                </div>
              </td>
              <td>
                <div class="d-flex align-items-center gap-2">
                  <span
                    class="badge font-14"
                    [ngStyle]="{ background: item.stage.bgColor, color: (item.stage.bgColor | readableText) }"
                  >
                    {{ item?.stage?.name }}
                  </span>
                </div>
              </td>

              <!-- Score (RF-05) — unavailable says so, never a zero (RNF-09, AC-23). -->
              <td class="text-center">
                @if (item?.leadQuality?.status === 'OK' && $safeNavigationMigration(item?.leadQuality?.score) !== null) {
                  <span class="badge bg-soft-primary text-primary fw-bold">
                    {{ item.leadQuality.score }}
                  </span>
                  @if (item?.leadQuality?.qualification) {
                    <br />
                    <small class="text-muted">
                      {{ qualificationLabel(item) | translate }}
                    </small>
                  }
                } @else {
                  <span class="text-muted" [matTooltip]="'kanban.radar.qualityUnavailableTooltip' | translate">—</span>
                }
              </td>

              <!--
                Urgency (RF-13, RF-14) — icon + text, legible without colour.
                Suppressed on a closed deal: nothing is pending on a won or lost
                opportunity, so it can be neither overdue nor stagnant.
              -->
              <td>
                @if (item?.urgency && !isClosed(item)) {
                  <span
                    class="badge d-inline-flex align-items-center gap-1"
                    [ngClass]="'bg-' + urgencyContext(item) + ' bg-opacity-75 text-white'"
                    [attr.aria-label]="urgencyLabel(item) | translate"
                  >
                    <i [class]="urgencyIcon(item)" class="font-12"></i>
                    <span class="font-11">{{ urgencyLabel(item) | translate }}</span>
                  </span>
                }
                @if (item?.stagnant && !isClosed(item)) {
                  <span
                    class="badge bg-soft-dark text-dark ms-1 font-11"
                    [matTooltip]="stagnantTooltip(item) | translate"
                  >
                    {{ 'kanban.radar.stagnant' | translate }}
                  </span>
                }
                <!-- E4 RF-17: the next action, as the entry point of its conclusion; no time when untimed (RF-05). -->
                @if (item?.nextAction?.dueAt && !isClosed(item)) {
                  <br />
                  <button
                    type="button"
                    class="btn btn-link p-0 border-0 text-muted font-12 d-inline-flex align-items-center text-decoration-none"
                    [matTooltip]="'kanban.radar.concludeNextAction' | translate"
                    [attr.aria-label]="'kanban.radar.concludeNextAction' | translate"
                    (click)="openConcludeDialog(item, $event)"
                  >
                    <i class="mdi mdi-flag-outline me-1"></i>
                    {{ item.nextAction.dueAt | date: nextActionFormat(item) }}
                  </button>
                }
              </td>

              <!-- Last interaction (RF-10). -->
              <td>
                @if (item?.lastInteraction?.at) {
                  <span class="font-13">{{ item.lastInteraction.at | date: 'd MMM' }}</span>
                  <br />
                  <small class="text-muted">{{ lastInteractionLabel(item) | translate }}</small>
                } @else {
                  <span class="text-muted">—</span>
                }
              </td>

              <td>
                {{ getProdutctsNames(item.product) }}
              </td>
              <td>
                <div class="dropdown d-flex justify-content-end p-2">
                  <a
                    href="#"
                    class="dropdown-toggle text-muted arrow-none"
                    data-bs-display="static"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                    (click)="$event.stopPropagation()"
                  >
                    <i
                      class="mdi mdi-dots-vertical font-18 text-primary-emphasis"
                    ></i>
                  </a>
                  <div
                    class="dropdown-menu dropdown-menu-end"
                    style="z-index: 1050"
                  >
                    <a
                      href="javascript:void(0);"
                      class="dropdown-item"
                      (click)="
                        openModalCard(item.stage, item);
                        $event.stopPropagation()
                      "
                    >
                      <i class="mdi mdi-pencil me-1"></i>
                      {{ 'kanban.cardList.edit' | translate }}
                    </a>
                    @if (
                      item.stage?.lostDeal !== true &&
                      item.stage?.wonDeal !== true
                    ) {
                      <a
                        href="javascript:void(0);"
                        class="dropdown-item"
                        (click)="
                          openTransferCardModal($event, item, 'move');
                          $event.stopPropagation()
                        "
                      >
                        <i class="mdi mdi-arrow-u-right-top me-1"></i>
                        {{ 'kanban.cardList.moveTo' | translate }}
                      </a>
                    } @else {
                      @if (!item.cloned) {
                        <a
                          href="javascript:void(0);"
                          class="dropdown-item"
                          (click)="
                            openTransferCardModal($event, item, 'clone');
                            $event.stopPropagation()
                          "
                        >
                          <i class="mdi mdi-file-multiple me-1"></i>
                          {{ 'kanban.cardList.clone' | translate }}
                        </a>
                      }
                    }
                    @if (!isAttendant()) {
                      <a
                        href="javascript:void(0);"
                        class="dropdown-item text-danger"
                        (click)="deleteCard(item); $event.stopPropagation()"
                      >
                        <i class="mdi mdi-delete me-1"></i>
                        {{ 'kanban.cardList.remove' | translate }}
                      </a>
                    }
                  </div>
                </div>
              </td>
            </tr>
          }
        </tbody>
      </table>

      @if (pager.total > pager.perPage) {
        <paginator [pager]="pager" (onPager)="loadPage($event)"></paginator>
      }
    </div>
  }
</div>
